import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaigns } from '../lib/api/campaigns';
import { useBrands } from '../lib/api/brands';
import { CampaignType } from '../lib/types';
import { CAMPAIGN_TYPES } from '../lib/enums';
import { fmtRange, toISODate, today } from '../lib/dates';
import { BrandStack } from '../lib/brandAvatars';
import { Button } from '../components/ui/Button';
import { HowItWorksLink } from '../components/ui/HowItWorksLink';
import { Icon } from '../components/ui/Icon';
import { Twemoji } from '../components/ui/Twemoji';
import { Chip } from '../components/ui/Chip';
import CreateCampaignModal from '../components/modals/CreateCampaignModal';
import CampaignCalendar from '../components/campaigns/CampaignCalendar';
import { GanttTimeline } from '../components/gantt/GanttTimeline';
import { splitCampaignName } from '../lib/campaignName';

type ViewMode = 'list' | 'calendar' | 'timeline';

const VIEWS: { key: ViewMode; label: string; icon: 'campaign' | 'calendar' | 'trending' }[] = [
  { key: 'list', label: 'List', icon: 'campaign' },
  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
  { key: 'timeline', label: 'Timeline', icon: 'trending' },
];

type SortMode = 'date-asc' | 'date-desc' | 'name-asc' | 'name-desc';

const SORTS: { key: SortMode; label: string }[] = [
  { key: 'date-asc', label: 'Upcoming first' },
  { key: 'date-desc', label: 'Most recent first' },
  { key: 'name-asc', label: 'Name A–Z' },
  { key: 'name-desc', label: 'Name Z–A' },
];

const Campaigns = () => {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<CampaignType | null>(null);
  const [view, setView] = useState<ViewMode>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [hidePast, setHidePast] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortMode>('date-asc');
  const { data: allCampaigns = [], isLoading } = useCampaigns({
    type: typeFilter ?? undefined,
  });
  const { data: brands = [] } = useBrands();
  const brandById = new Map(brands.map((b) => [b.id, b]));

  const todayStr = toISODate(today());
  // An undated campaign is never "past" — it has no end date yet.
  const pastCount = useMemo(
    () => allCampaigns.filter((c) => !!c.end_date && c.end_date < todayStr).length,
    [allCampaigns, todayStr],
  );
  const campaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = hidePast
      ? allCampaigns.filter((c) => !c.end_date || c.end_date >= todayStr)
      : allCampaigns;
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q));

    const sorted = [...list];
    if (sortBy.startsWith('name')) {
      sorted.sort((a, b) => (sortBy === 'name-asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)));
    } else {
      // Undated ("To plan") campaigns lead in both date orders — they're
      // the ones still waiting for a decision.
      sorted.sort((a, b) => {
        if (!a.start_date && !b.start_date) return a.name.localeCompare(b.name);
        if (!a.start_date) return -1;
        if (!b.start_date) return 1;
        return sortBy === 'date-asc'
          ? a.start_date.localeCompare(b.start_date)
          : b.start_date.localeCompare(a.start_date);
      });
    }
    return sorted;
  }, [allCampaigns, hidePast, todayStr, search, sortBy]);

  /** Column header that sorts the list — click to sort, click again to
   *  reverse. Shares the same state as the sort picker so both stay in sync. */
  const SortHeader = ({ label, field }: { label: string; field: 'name' | 'date' }) => {
    const active = sortBy.startsWith(field);
    const asc = sortBy === `${field}-asc`;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setSortBy((active && asc ? `${field}-desc` : `${field}-asc`) as SortMode);
        }}
        title={`Sort by ${label.toLowerCase()}`}
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          color: active ? 'var(--ink-2)' : 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {label}
        <span style={{ fontSize: 8, opacity: active ? 1 : 0.3 }}>
          {active && !asc ? '▼' : '▲'}
        </span>
      </button>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Portfolio</div>
          <h1 className="page-title">Campaigns</h1>
          <p className="page-sub">
            {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'}
            {typeFilter ? ` · ${typeFilter}` : ''}.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HowItWorksLink section="campaigns" />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> New campaign
          </Button>
        </div>
      </div>

      <div
        className="filter-bar"
        style={{ marginBottom: 6 }}
      >
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`filter-pill ${view === v.key ? 'active' : ''}`}
            onClick={() => setView(v.key)}
          >
            <Icon name={v.icon} size={12} />
            {v.label}
          </button>
        ))}
      </div>

      <div className="filter-bar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <button
            type="button"
            className={`filter-pill ${typeFilter === null ? 'active' : ''}`}
            onClick={() => setTypeFilter(null)}
          >
            All types
          </button>
          {CAMPAIGN_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`filter-pill ${typeFilter === t ? 'active' : ''}`}
              onClick={() => setTypeFilter(t as CampaignType)}
            >
              <span
                className="dot"
                style={{
                  background:
                    t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)',
                }}
              />
              {t}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 8, display: 'inline-flex', color: 'var(--ink-4)', pointerEvents: 'none' }}>
              <Icon name="search" size={12} />
            </span>
            <input
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns…"
              style={{
                fontSize: 12,
                paddingTop: 4,
                paddingBottom: 4,
                paddingLeft: 26,
                paddingRight: search ? 24 : 8,
                width: 190,
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                title="Clear search"
                style={{
                  position: 'absolute', right: 5, display: 'inline-flex',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'var(--ink-4)', padding: 2,
                }}
              >
                <Icon name="x" size={11} />
              </button>
            )}
          </div>
          <select
            className="input"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortMode)}
            title="Sort the campaign list"
            style={{
              fontSize: 12,
              paddingTop: 4,
              paddingBottom: 4,
              paddingLeft: 8,
              paddingRight: 24,
              width: 'auto',
            }}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <button
            type="button"
            className={`filter-pill ${hidePast ? 'active' : ''}`}
            onClick={() => setHidePast((v) => !v)}
            title={hidePast ? 'Past campaigns are hidden' : 'Past campaigns are visible'}
          >
            <Icon name="clock" size={12} />
            {hidePast ? `Hide past${pastCount ? ` (${pastCount})` : ''}` : 'Showing past'}
          </button>
        </div>
      </div>

      {view === 'list' && (
        <div className="card" style={{ marginTop: 8 }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>Loading campaigns…</div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>
                {search ? `No campaigns match "${search}".` : 'No campaigns match your filters.'}
              </p>
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Icon name="plus" size={13} /> New campaign
              </Button>
            </div>
          ) : (
            <div className="row-list">
              <div
                className="row"
                style={{
                  gridTemplateColumns: '1fr 110px 170px 140px 28px',
                  background: 'var(--bg-subtle)',
                  color: 'var(--ink-4)',
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                  cursor: 'default',
                }}
              >
                <div><SortHeader label="Campaign" field="name" /></div>
                <div>Type</div>
                <div><SortHeader label="Dates" field="date" /></div>
                <div>Brands</div>
                <div />
              </div>
              {campaigns.map((c) => (
                <div
                  key={c.id}
                  className="row"
                  style={{ gridTemplateColumns: '1fr 110px 170px 140px 28px' }}
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                >
                  <div>
                    {(() => {
                      const { eyebrow, title } = splitCampaignName(c.name);
                      return (
                        <div className="row-title">
                          {c.emoji && <span style={{ marginRight: 8 }}><Twemoji emoji={c.emoji} size={18} /></span>}
                          {eyebrow && (
                            <span style={{ color: 'var(--ink-4)', fontWeight: 450, marginRight: 6 }}>
                              {eyebrow}
                            </span>
                          )}
                          {title}
                        </div>
                      );
                    })()}
                    <div className="row-meta">
                      {c.brand_details?.length || 0} brand{(c.brand_details?.length ?? 0) === 1 ? '' : 's'}
                      {c.parent ? ' · nested' : ''}
                    </div>
                  </div>
                  <div><Chip kind="type" value={c.type} /></div>
                  <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {c.start_date && c.end_date ? fmtRange(c.start_date, c.end_date) : (
                      <span style={{
                        fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em',
                        color: '#a87800', background: '#fff7e0',
                        padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                      }}>
                        To plan
                      </span>
                    )}
                  </div>
                  <div>
                    <BrandStack
                      brands={(c.brands as string[]).map((bid) => brandById.get(bid)).filter(Boolean) as any}
                      max={4}
                    />
                  </div>
                  <div><Icon name="chevron" size={14} className="icon" /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'calendar' && (
        <div style={{ marginTop: 8 }}>
          {isLoading ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
              Loading campaigns…
            </div>
          ) : (
            <CampaignCalendar
              campaigns={campaigns}
              onCampaignClick={(id) => navigate(`/campaigns/${id}`)}
            />
          )}
        </div>
      )}

      {view === 'timeline' && (
        <div style={{ marginTop: 8 }}>
          {isLoading ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
              Loading campaigns…
            </div>
          ) : campaigns.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
              No campaigns match your filters.
            </div>
          ) : (
            <GanttTimeline
              campaigns={campaigns}
              onCampaignClick={(id) => navigate(`/campaigns/${id}`)}
              rangeWeeks={16}
              startOffsetWeeks={-2}
            />
          )}
        </div>
      )}

      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => navigate(`/campaigns/${id}`)}
      />
    </div>
  );
};

export default Campaigns;
